import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useEvents } from "./use-events";
import { useToast } from "@/components/ui/use-toast";
import {
  ExportQueuedPayload,
  ExportStartedPayload,
  ExportProgressPayload,
  ExportCompletedPayload,
  ExportFailedPayload,
} from "@/types/exports";

export function useExportUpdates() {
  const { addEventListener } = useEvents();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  useEffect(() => {
    // Handle export queued
    const unsubscribeQueued = addEventListener<ExportQueuedPayload>(
      "EXPORT_QUEUED",
      (payload) => {
        console.log("Export queued:", payload);
        // Invalidate export history to show the new export
        queryClient.invalidateQueries({ queryKey: ["export-history"] });
      },
    );

    // Handle export started
    const unsubscribeStarted = addEventListener<ExportStartedPayload>(
      "EXPORT_STARTED",
      (payload) => {
        console.log("Export started:", payload);
        // Invalidate export history to update status
        queryClient.invalidateQueries({ queryKey: ["export-history"] });
        queryClient.invalidateQueries({
          queryKey: ["export", payload.exportId],
        });
      },
    );

    // Handle export progress
    const unsubscribeProgress = addEventListener<ExportProgressPayload>(
      "EXPORT_PROGRESS",
      (payload) => {
        console.log("Export progress:", payload);
        // Could show progress in UI if needed
        queryClient.invalidateQueries({
          queryKey: ["export", payload.exportId],
        });
      },
    );

    // Handle export completed
    const unsubscribeCompleted = addEventListener<ExportCompletedPayload>(
      "EXPORT_COMPLETED",
      (payload) => {
        console.log("Export completed:", payload);
        // Invalidate queries and show success toast
        queryClient.invalidateQueries({ queryKey: ["export-history"] });
        queryClient.invalidateQueries({
          queryKey: ["export", payload.exportId],
        });

        toast({
          title: "Export completed",
          description: "Your export is ready for download.",
        });
      },
    );

    // Handle export failed
    const unsubscribeFailed = addEventListener<ExportFailedPayload>(
      "EXPORT_FAILED",
      (payload) => {
        console.log("Export failed:", payload);
        // Invalidate queries and show error toast
        queryClient.invalidateQueries({ queryKey: ["export-history"] });
        queryClient.invalidateQueries({
          queryKey: ["export", payload.exportId],
        });

        toast({
          title: "Export failed",
          description:
            payload.error || "The export process encountered an error.",
          variant: "destructive",
        });
      },
    );

    // Cleanup function
    return () => {
      unsubscribeQueued();
      unsubscribeStarted();
      unsubscribeProgress();
      unsubscribeCompleted();
      unsubscribeFailed();
    };
  }, [addEventListener, queryClient, toast]);
}
